/// <amd-module name="Repos/Changes/Components/ChangeSummaryCard" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { useChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { FileDiffBlockChangeType, IChange, IFileDiff, IFileDiffParameters } from "Repos/ChangesCommon/Contracts";
import { VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation, DiffViewerSide } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IDiffSummaryDiscussionIntegration,
    IDiffSummaryDiscussionWidget,
    ILineInfo,
    LineInfoType
} from "Repos/Discussion/Common/DiscussionContracts";
import { HighlightableSpan } from "Repos/Highlight/Components/HighlightableSpan";
import { IObservableValue, IReadonlyObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, useComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { Diff } from "./Diff/Diff";
import { IDiffSummaryDiscussionWidgetProvider } from "./Diff/DiffBlock";

export class DiscussionWidgetProvider implements IDiffSummaryDiscussionWidgetProvider {
    private diffIntegration: IDiffSummaryDiscussionIntegration | undefined;

    constructor(diffIntegration?: IDiffSummaryDiscussionIntegration) {
        this.diffIntegration = diffIntegration;
    }

    public canAddComments(): boolean {
        return !!this.diffIntegration && this.diffIntegration.canAddComments;
    }

    public commentsSupported(): boolean {
        return !!this.diffIntegration;
    }

    public getDisplayLineNumber(lineNumber: number, side: DiffViewerSide) {
        return this.diffIntegration && this.diffIntegration.getDisplayLineNumber(lineNumber, side);
    }

    public getWidgets(): IReadonlyObservableArray<IDiffSummaryDiscussionWidget> | IDiffSummaryDiscussionWidget[] {
        return this.diffIntegration ? this.diffIntegration.getWidgets() : [];
    }

    public getWidgetsByLine(
        displayLineNumber: number,
        orientation: DiffViewerOrientation,
        addToOriginalDiff: boolean
    ): IDiffSummaryDiscussionWidget[] {
        const widgets = ObservableLike.getValue(this.getWidgets());
        return widgets.filter(w => w.belongsToLine(displayLineNumber, addToOriginalDiff, LineInfoType.Display) && !w.hideOnOrientation(orientation));
    }

    public getWidgetsByLineRange(
        displayLineNumber: number,
        size: number,
        orientation: DiffViewerOrientation,
        addToOriginalDiff: boolean
    ): IDiffSummaryDiscussionWidget[] {
        const result: IDiffSummaryDiscussionWidget[] = [];
        for (let i = displayLineNumber; i < displayLineNumber + size; i++) {
            result.push(...this.getWidgetsByLine(i, orientation, addToOriginalDiff));
        }
        return result;
    }

    public initializeLineMapping(diff: IFileDiff): void {
        const modifiedToDisplay: { [key: number]: number } = {};
        const originalToDisplay: { [key: number]: number } = {};

        let currentDisplayLine = 0;

        const addToMapping = (fileToDisplayMapping: { [key: number]: number }, base: number, size: number) => {
            for (let i = 0; i < size; i++) {
                fileToDisplayMapping[base + i] = currentDisplayLine + i;
            }
        };

        if (!diff.whitespaceChangesOnly) {
            for (const diffBlock of diff.blocks) {
                diffBlock.mLine && addToMapping(modifiedToDisplay, diffBlock.mLine, diffBlock.mLines.length);
                diffBlock.oLine && addToMapping(originalToDisplay, diffBlock.oLine, diffBlock.oLines.length);

                const mLineLength = (diffBlock.mLines || []).length;
                const oLineLength = (diffBlock.oLines || []).length;

                currentDisplayLine += Math.max(mLineLength, oLineLength);
            }
        }

        this.diffIntegration && this.diffIntegration.initializeLineMapping(originalToDisplay, modifiedToDisplay);
    }

    public renderAddCommentWidget(lineInfo: ILineInfo, addToOriginalDiff: boolean, className: string): JSX.Element | undefined {
        return this.diffIntegration && this.diffIntegration.renderAddCommentWidget(lineInfo, addToOriginalDiff, className);
    }

    public addComment(lineInfo: ILineInfo, addToOriginalDiff: boolean): void {
        this.diffIntegration && this.diffIntegration.addComment(lineInfo, addToOriginalDiff);
    }
}

const diffTypes = VersionControlChangeType.Edit | VersionControlChangeType.Add | VersionControlChangeType.Delete | VersionControlChangeType.Undelete;

export interface IChangeSummaryCardProps {
    change: IChange;
    compareVersion?: string;
    diffIntegration?: IDiffSummaryDiscussionIntegration;
    highlightText?: string;
    fileUrl?: string;
    getDiffParameters?: () => IFileDiffParameters;
    onViewFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}

export function ChangeSummaryCard<TVersionDescriptor>(props: IChangeSummaryCardProps): JSX.Element {
    const { change, compareVersion, fileUrl, getDiffParameters, diffIntegration, highlightText, onViewFile } = props;

    const [cardCollapsed] = React.useState(new ObservableValue(false));
    const [fileDiff] = React.useState<IObservableValue<IFileDiff | undefined>>(new ObservableValue(undefined));
    const [diffWidth] = React.useState(new ObservableValue<number | undefined>(undefined));
    const [loading] = React.useState(new ObservableValue(false));

    const widgetProvider = new DiscussionWidgetProvider(diffIntegration);
    if (diffIntegration) {
        diffIntegration.setDiffWidth(diffWidth);

        if (fileDiff.value && !fileDiff.value.identicalContent && widgetProvider) {
            // make sure widget provider line mapping is initialized in case this isnt first load
            widgetProvider.initializeLineMapping(fileDiff.value);
        }
    }

    const { pageContext } = useComponentContext();
    const { filesService } = useChangesContext<TVersionDescriptor>();

    const fetchDiff = (forceLoad: boolean = false): void => {
        let diffParameters: IFileDiffParameters;
        if (getDiffParameters) {
            diffParameters = getDiffParameters();
        } else {
            if (!(change.changeType & diffTypes)) {
                // A pure rename doesn't need a fetch, files are identical
                fileDiff.value = {
                    identicalContent: true
                } as IFileDiff;

                return;
            }

            const currentVersion = filesService.convertVersionToDescriptor(change.commitId);
            let originalPath = change.sourcePath || change.path;
            if ((change.changeType & VersionControlChangeType.Add) === VersionControlChangeType.Add) {
                originalPath = "";
            }

            let modifiedPath = "";
            if ((change.changeType & VersionControlChangeType.Delete) !== VersionControlChangeType.Delete) {
                modifiedPath = change.path;
            }

            diffParameters = {
                includeCharDiffs: true,
                modifiedPath: modifiedPath,
                modifiedVersion: filesService.convertDescriptorToVersion(currentVersion),
                originalPath: originalPath,
                originalVersion: compareVersion,
                partialDiff: true,
                forceLoad: forceLoad
            };
        }

        loading.value = true;
        filesService.getFileDiffPromise(diffParameters).then(diff => {
            diff && widgetProvider.initializeLineMapping(diff);
            fileDiff.value = diff;
        }).finally(() => loading.value = false);
    };

    const onCollapseClick = () => (cardCollapsed.value = !cardCollapsed.value);

    return (
        <Observer diff={fileDiff} loading={loading}>
            {(props: { diff: IFileDiff | undefined, loading: boolean }) => {
                return (
                    <Card
                        className="repos-summary-header flex-noshrink"
                        key={change.path}
                        collapsible={true}
                        collapsed={cardCollapsed}
                        onCollapseClick={onCollapseClick}
                        renderHeader={() => (
                            <ChangeSummaryHeader
                                change={change}
                                diff={props.diff}
                                fileUrl={fileUrl}
                                highlightText={highlightText}
                                onViewFile={onViewFile}
                                pageContext={pageContext}
                            />
                        )}
                    >
                        <div className="change-summary-card-content flex-column flex-grow scroll-hidden">
                            <Diff
                                diff={props.diff}
                                diffWidth={diffWidth}
                                fileUrl={fileUrl}
                                getCommentPositions={diffIntegration ? () => diffIntegration.getThreads().map(t => t.threadContext!) : undefined}
                                onViewFile={onViewFile}
                                onVisible={fetchDiff}
                                loading={props.loading}
                                widgetProvider={widgetProvider}
                            />
                        </div>
                    </Card>
                );
            }}
        </Observer>
    );
}

interface IChangeSummaryHeaderProps {
    change: IChange;
    diff?: IFileDiff;
    fileUrl?: string;
    highlightText?: string;
    onViewFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
    pageContext: IVssPageContext;
}

const ChangeSummaryHeader = (props: IChangeSummaryHeaderProps): JSX.Element => {
    const { change, diff, fileUrl, highlightText, onViewFile, pageContext } = props;

    let linesAdded = 0;
    let linesRemoved = 0;
    if (diff && !diff.imageComparison && diff.lineCharBlocks) {
        for (const block of diff.lineCharBlocks) {
            const lineChange = block.lineChange;
            if (lineChange.changeType === FileDiffBlockChangeType.Add) {
                linesAdded += lineChange.mLinesCount;
            } else if (lineChange.changeType === FileDiffBlockChangeType.Delete) {
                linesRemoved += lineChange.oLinesCount;
            } else if (lineChange.changeType === FileDiffBlockChangeType.Edit) {
                linesAdded += lineChange.mLinesCount;
                linesRemoved += lineChange.oLinesCount;
            }
        }
    }
    return (
        <div className="flex-row flex-start flex-grow scroll-hidden text-ellipsis sticky">
            <div className="body-s secondary-text repos-change-summary-file-icon-container">
                <Icon {...change.iconProps} className={css("repos-change-summary-file-icon", change.iconProps.className)} />
            </div>
            <div className="flex-column text-ellipsis margin-right-4">
                <div className="flex-row rhythm-horizontal-8">
                    <div className="flex flex-center body-m font-weight-semibold text-ellipsis">
                        <HighlightableSpan className="text-ellipsis" highlight={highlightText} text={change.name} />
                    </div>
                    {diff && (diff.modifiedFileTruncated || diff.originalFileTruncated) ? (
                        <Icon
                            tabIndex={0}
                            iconName="Warning"
                            ariaLabel={Resources.SummaryCountsFileTooLarge}
                            tooltipProps={{ text: Resources.SummaryCountsFileTooLarge }}
                        />
                    ) : (
                        <span role="heading" aria-level={3}>
                            <span className="screen-reader-only">{change.path}</span>
                            {linesRemoved > 0 && (
                                <span className="repos-compare-removed-lines font-weight-semibold">
                                    <Tooltip text={format(Resources.LinesRemovedTooltip, linesRemoved)}>
                                        <span>-{linesRemoved}</span>
                                    </Tooltip>
                                </span>
                            )}
                            {linesAdded > 0 && (
                                <span className="repos-compare-added-lines font-weight-semibold">
                                    <Tooltip text={format(Resources.LinesAddedTooltip, linesAdded)}>
                                        <span>+{linesAdded}</span>
                                    </Tooltip>
                                </span>
                            )}
                        </span>
                    )}
                    {diff && diff.modifiedFileEncoding !== diff.originalFileEncoding && (
                    <div className="flex flex-center body-s secondary-text text-ellipsis">
                        <Tooltip text={format(Resources.EncodingChangedTooltip, diff.originalFileEncoding, diff.modifiedFileEncoding)}>
                            <div className="text-ellipsis">{format(Resources.EncodingChanged, diff.originalFileEncoding, diff.modifiedFileEncoding)}</div>
                        </Tooltip>
                    </div>
                )}
                </div>
                <Tooltip overflowOnly={true}>
                    <div className="body-s secondary-text text-ellipsis">{change.path}</div>
                </Tooltip>
                {change.sourcePath && change.sourcePath !== change.path && (
                    <div className="body-s secondary-text flex-column margin-top-8">
                        <div>{change.changeType & VersionControlChangeType.Branch ? Resources.BranchedFrom : Resources.RenamedFrom}</div>
                        <div className="text-ellipsis">{change.sourcePath}</div>
                    </div>
                )}
            </div>
            {fileUrl && (
                <div className="flex-row flex-grow justify-end">
                    <Button
                        className="flex-end"
                        href={fileUrl}
                        onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                            onViewFile ? onViewFile(event) : onClickFPS(pageContext, fileUrl, true, event);
                        }}
                    >
                        {Resources.View}
                    </Button>
                </div>
            )}
        </div>
    );
};
